import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { ModelModel } from '@models/api-models/model'
import { ComponentVariantModel } from '@models/component-variant'
import { CustomizerMenuPartModel } from '@models/customizer-menu-part'
import { ModelGroupModel } from '@models/model-group'
import { ModelSizeModel } from '@models/model-size'

@Component({
    selector: 'realtime-wrapper',
    template: '<realtime-modelviewer></realtime-modelviewer>',
    styleUrls: ['./realtime-wrapper.scss'],
    imports: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true
})

export class RealtimeWrapperComponent implements OnInit, OnChanges, OnDestroy {

	@Input() modelGroup?: ModelGroupModel
	@Input() model?: ModelModel
	@Input() modelSize?: ModelSizeModel
	@Input() variants?: Array<ComponentVariantModel>
	@Input() engraving: string
	@Input() isPrecious: boolean = false

	@Input() currentSubComponent: CustomizerMenuPartModel | undefined;
	@Input() currentMainComponent?: CustomizerMenuPartModel;

	private modelViewer: ModelViewer;
	private configurationTimeout: number;

	private isModelViewerDoneLoading: boolean = false;

	elementRef: ElementRef | undefined = inject(ElementRef);

	public get isModelViewerLoaded(): boolean {
		return this.isModelViewerDoneLoading;
	}

	ngOnInit(): void {
		this.isModelViewerDoneLoading = false;

		this.modelViewer = this.elementRef?.nativeElement.querySelector("realtime-modelviewer") as ModelViewer;
		this.modelViewer.theme = this.isPrecious ? "Precious" : "Titanium";

		this.updateConfiguration();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.modelViewer) return;
		this.isModelViewerDoneLoading = false;

		if (changes["currentSubComponent"] || changes["currentMainComponent"]) {
			this.modelViewer.view = [this.currentMainComponent?.name, this.currentSubComponent?.name].filter(Boolean).join("/");
		} else {
			if (changes["model"]) {
				clearTimeout(this.configurationTimeout);
				this.configurationTimeout = window.setTimeout(this.updateConfiguration.bind(this), 1000);
			} else {
				clearTimeout(this.configurationTimeout);
				this.updateConfiguration();
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy();
	}

	public destroy() {
		if (this.elementRef && this.elementRef.nativeElement) {
			this.elementRef.nativeElement.remove();
			this.elementRef = undefined;
			console.log('removed!');

		}
	}

	async updateConfiguration() {
		const config: FrameConfiguration = {
			productline: this.modelGroup?.Code as Productline,
			isRimless: this.modelGroup?.IsRimless,
			engraving: this.engraving,
			components: {}
		};

		config.components.MODEL = { design: this.model?.Code?.toUpperCase(), size: this.modelSize?.Code };

		if (this.variants)
			for (const variant of this.variants) {
				const componentType = variant.Component.Code as ComponentType;

				config.components[componentType] = { design: variant.Code };
				if (variant.VariantColor)
					config.components[componentType].color = variant.VariantColor.Color?.ColorLine?.Code?.replace(" ", "-") + "/" + variant.VariantColor.Color?.Code;

				if (variant.VariantSize)
					config.components[componentType].size = variant.VariantSize.Size;
			}

		this.modelViewer.configuration = config;
		console.log(config);
		if(!customElements.get('realtime-modelviewer')) return console.warn("ModelViewer not loaded, cannot load configuration");
		await this.modelViewer.load();
		this.isModelViewerDoneLoading = true;
	}
}

type Theme = "Titanium" | "Precious"
type CameraAngle = "Front" | "Perspective" | "Side" | "Engraving"
export type ComponentType = "MODEL" | "BRIDGE" | "CLIP" | "CLIP-ON" | "COVER" | "CROSSBAR" | "FRONT" | "GEMSTONE" | "GROOVE" | "INNERRIM" | "LENSES" | "LOWERRIM" | "NOSEPAD" | "TEMPLE" | "UPPERRIM"
export type Productline = "SUN" | "AIR" | "ACETANIUM" | "STRIP" | "NOW" | "TT" | "HORN" | "RIM"

type ComponentConfiguration = {
	design?: string
	size?: string
	color?: string
}

type FrameConfiguration = {
	productline: Productline
	engraving?: string
	isRimless?: boolean
	components: {
		[P in ComponentType]?: ComponentConfiguration
	}
}

interface ModelViewer extends HTMLElement {
	canvas: HTMLCanvasElement
	theme: Theme
	cameraAngle: CameraAngle
	configuration: FrameConfiguration
	view: string
	useAnimations: boolean
	load(): Promise<void>
}